import { useState } from 'react';
import { Play, Loader2, CheckCircle, AlertCircle, Info, XCircle } from 'lucide-react';

interface ScanStatus {
  id: string;
  status: 'running' | 'completed' | 'failed';
  progress: number;
  currentBlock: number;
  totalBlocks: number;
  addressesFound: number;
  poolsDetected: number;
  transactionsProcessed: number;
  startTime: number;
  message: string;
  error?: string;
}

export default function Scanner() {
  const [tokenAddress, setTokenAddress] = useState('0xc708d6f2153933daa50b2d0758955be0a93a8fec'); // vstBTC default
  const [chain, setChain] = useState('polygon');
  const [maxHops, setMaxHops] = useState(10);
  const [daysToScan, setDaysToScan] = useState(1);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<ScanStatus | null>(null);
  const [scanId, setScanId] = useState<string | null>(null);

  // Format elapsed time
  const formatTime = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const handleStartScan = async (e: React.FormEvent) => {
    e.preventDefault();
    setScanning(true);
    setScanStatus(null);

    try {
      // Call API to start scan (runs on server-side to avoid CORS)
      const response = await fetch('/api/scan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tokenAddress,
          chain,
          daysToScan,
          maxHops,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to start scan');
      }

      const result = await response.json();
      const newScanId = result.scanId;
      setScanId(newScanId);

      // Poll for status updates from API
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch(`/api/scan/${newScanId}`);
          if (statusResponse.ok) {
            const status = await statusResponse.json();
            setScanStatus(status);
            
            // Stop polling if scan is complete or failed
            if (status.status !== 'running') {
              clearInterval(pollInterval);
              setScanning(false);
            }
          }
        } catch (err) {
          console.error('Error polling scan status:', err);
        }
      }, 2000); // Poll every 2 seconds

    } catch (error) {
      console.error('Failed to start scan:', error);
      setScanStatus({
        id: 'error',
        status: 'failed',
        progress: 0,
        currentBlock: 0,
        totalBlocks: 0,
        addressesFound: 0,
        poolsDetected: 0,
        transactionsProcessed: 0,
        startTime: Date.now(),
        message: 'Failed to start scan',
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      setScanning(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent">
          Token Scanner
        </h1>
        <p className="text-gray-400 text-lg">
          Scan blockchain for abuse patterns and pool detection
        </p>
      </div>

      {/* Scan Configuration */}
      <div className="p-6 rounded-xl bg-gray-800/50 border border-gray-700 backdrop-blur-sm mb-6">
        <h2 className="text-xl font-bold text-white mb-6">
          Scan Configuration
        </h2>

        <form onSubmit={handleStartScan} className="space-y-6">
          {/* Token Address */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Token Contract Address
            </label>
            <input
              type="text"
              placeholder="0x..."
              value={tokenAddress}
              onChange={(e) => setTokenAddress(e.target.value)}
              required
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              disabled={scanning}
            />
            <p className="mt-2 text-sm text-gray-400">
              Pre-filled with vstBTC token address
            </p>
          </div>

          {/* Blockchain */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Blockchain Network
            </label>
            <select
              value={chain}
              onChange={(e) => setChain(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              disabled={scanning}
            >
              <option value="polygon">Polygon</option>
              <option value="ethereum">Ethereum</option>
            </select>
          </div>

          {/* Days to Scan */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Days to Scan: <span className="text-blue-400 font-bold">{daysToScan} {daysToScan === 1 ? 'day' : 'days'}</span>
            </label>
            <input
              type="range"
              min="1"
              max="60"
              value={daysToScan}
              onChange={(e) => setDaysToScan(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              disabled={scanning}
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>1 day</span>
              <span>60 days</span>
            </div>
            <p className="mt-2 text-sm text-gray-400">
              {daysToScan === 60 ? '⚠️ 60-day scan may take 1-2 hours due to rate limiting' : 
               daysToScan > 7 ? `Estimated time: ~${Math.round(daysToScan * 2)} minutes` :
               'Quick scan (a few minutes)'}
            </p>
          </div>

          {/* Max Hops */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Maximum Hops: <span className="text-blue-400 font-bold">{maxHops}</span>
            </label>
            <input
              type="range"
              min="1"
              max="20"
              value={maxHops}
              onChange={(e) => setMaxHops(Number(e.target.value))}
              className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
              disabled={scanning}
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>1 hop</span>
              <span>20 hops</span>
            </div>
            <p className="mt-2 text-sm text-gray-400">
              {maxHops <= 3 ? '⚡ Fast - Direct transfers only' : 
               maxHops <= 10 ? '🔍 Balanced - Catches most abuse patterns' :
               '🎯 Deep - Maximum obfuscation detection'}
            </p>
          </div>

          {/* Start Button */}
          <button
            type="submit"
            disabled={scanning}
            className={`
              w-full flex items-center justify-center gap-3 px-6 py-4 rounded-lg font-semibold text-lg
              transition-all duration-200 shadow-lg
              ${scanning 
                ? 'bg-gray-600 cursor-not-allowed' 
                : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl hover:scale-[1.02]'
              }
              text-white
            `}
          >
            {scanning ? (
              <>
                <Loader2 size={24} className="animate-spin" />
                Scanning...
              </>
            ) : (
              <>
                <Play size={24} />
                Start Scan
              </>
            )}
          </button>
        </form>
      </div>

      {/* Scan Progress */}
      {scanStatus && scanStatus.status === 'running' && (
        <div className="mb-6 p-6 rounded-xl bg-gradient-to-br from-blue-500/10 to-green-500/10 border-2 border-blue-500/50 backdrop-blur-sm">
          <div className="flex items-start gap-6">
            <div className="p-4 rounded-full bg-blue-600 animate-pulse">
              <Loader2 size={24} className="animate-spin text-white" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-xl font-semibold text-white">
                  Scan in Progress
                </h3>
                <span className="px-4 py-1 rounded-full bg-green-600 text-white text-sm font-medium">
                  {scanStatus.progress}%
                </span>
              </div>
              <p className="text-gray-300 mb-4">
                {scanStatus.message}
              </p>
              
              {/* Progress Bar */}
              <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden mb-4">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-300 ease-out rounded-full"
                  style={{ width: `${scanStatus.progress}%` }}
                />
              </div>

              {/* Scan Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">
                    Blocks Scanned
                  </p>
                  <p className="text-lg font-semibold text-white">
                    {scanStatus.currentBlock.toLocaleString()} / {scanStatus.totalBlocks.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">
                    Addresses Found
                  </p>
                  <p className="text-lg font-semibold text-blue-400">
                    {scanStatus.addressesFound.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">
                    Transactions
                  </p>
                  <p className="text-lg font-semibold text-yellow-400">
                    {scanStatus.transactionsProcessed.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">
                    Time Elapsed
                  </p>
                  <p className="text-lg font-semibold text-white">
                    {formatTime(Date.now() - scanStatus.startTime)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan Complete */}
      {scanStatus && scanStatus.status === 'completed' && (
        <div className="mb-6 p-6 rounded-xl bg-green-900/20 border border-green-700 backdrop-blur-sm">
          <div className="flex items-start gap-4">
            <CheckCircle size={32} className="text-green-500 mt-1" />
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-green-300 mb-2">
                Scan Completed Successfully!
              </h3>
              <p className="text-gray-300 mb-4">
                {scanStatus.message}
              </p>
              <p className="text-gray-400 text-sm mb-4">
                Found {scanStatus.addressesFound} addresses and {scanStatus.transactionsProcessed} transactions
              </p>
              <div className="flex gap-3">
                <a 
                  href="/"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all"
                >
                  View Dashboard
                </a>
                <button
                  onClick={() => {
                    setScanStatus(null);
                    setScanId(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all"
                >
                  Start New Scan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Scan Failed */}
      {scanStatus && scanStatus.status === 'failed' && (
        <div className="mb-6 p-6 rounded-xl bg-red-900/20 border border-red-700 backdrop-blur-sm">
          <div className="flex items-start gap-4">
            <XCircle size={32} className="text-red-500 mt-1" />
            <div className="flex-1">
              <h3 className="text-xl font-semibold text-red-300 mb-2">
                Scan Failed
              </h3>
              <p className="text-gray-300 mb-4">
                {scanStatus.error || scanStatus.message || 'An unknown error occurred'}
              </p>
              <button
                onClick={() => {
                  setScanStatus(null);
                  setScanId(null);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
          <div className="flex items-start gap-3">
            <Info size={20} className="text-blue-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-300 mb-1">Multi-Hop Analysis</h3>
              <p className="text-sm text-gray-400">
                Tracks fund flows through 1-20 transaction hops
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
          <div className="flex items-start gap-3">
            <CheckCircle size={20} className="text-green-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-green-300 mb-1">Pool Detection</h3>
              <p className="text-sm text-gray-400">
                Identifies accumulation addresses automatically
              </p>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-yellow-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-300 mb-1">Abuse Signals</h3>
              <p className="text-sm text-gray-400">
                10+ detection patterns for comprehensive analysis
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-6 p-6 rounded-xl bg-gray-800/30 border border-gray-700">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <button 
            onClick={() => setDaysToScan(1)}
            className="px-4 py-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg text-left transition-all disabled:opacity-50"
            disabled={scanning}
          >
            <div className="font-medium text-white">Daily Scan</div>
            <div className="text-sm text-gray-400">Last 24 hours</div>
          </button>
          <button 
            onClick={() => setDaysToScan(7)}
            className="px-4 py-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg text-left transition-all disabled:opacity-50"
            disabled={scanning}
          >
            <div className="font-medium text-white">Weekly Scan</div>
            <div className="text-sm text-gray-400">Last 7 days</div>
          </button>
          <button 
            onClick={() => setDaysToScan(60)}
            className="px-4 py-3 bg-blue-700/30 hover:bg-blue-700/50 border border-blue-500/30 rounded-lg text-left transition-all disabled:opacity-50"
            disabled={scanning}
          >
            <div className="font-medium text-blue-300">Initial 60-Day Scan</div>
            <div className="text-sm text-gray-400">Full historical analysis</div>
          </button>
        </div>
      </div>
    </div>
  );
}
